import { Box, Stack, Typography, useTheme } from "@mui/material";
import { DekoxIcon } from "src/components";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { Button, IconButton, TooltipShortcut } from "@itk/mui-components";
import { getClickableProps } from "src/utils/keyboard-utils";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { FILTER_WIDTH } from "../../configurator-utils";
import { filterActions } from "./filter-store";
import { useEffect } from "react";
import { useParams } from "react-router";
import { FilterLoader } from "./filter-loader";
import { AttributesFilter, LabelsFilter } from "./components";
import { configuratorActions } from "../../configurator-store";
import { getAllFilters } from "./filter-types";

const mapStateToProps = (state: RootState) => ({
  isFilterOpen: state.pageReducer.combinedConfiguratorReducer.filterReducer.isFilterOpen,
  formData: state.pageReducer.combinedConfiguratorReducer.filterReducer.formData,
  appliedFilter: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.appliedFilter,
  filterState: state.pageReducer.combinedConfiguratorReducer.filterReducer.filterState
});

const ownActions = {
  setIsFilterOpen: filterActions.setIsFilterOpen,
  getFiltersRequest: filterActions.getFiltersRequest,
  resetFormData: filterActions.resetFormData,
  getConfigurationsRequest: configuratorActions.getConfigurationsRequest
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const FilterBase = (props: Props) => {
  const { isFilterOpen, setIsFilterOpen, getFiltersRequest, filterState, resetFormData, getConfigurationsRequest, formData, appliedFilter } = props;

  const theme = useTheme();
  const { projectId = "" } = useParams<{ projectId: string }>();

  const genericTranslationsT = useGenericTranslationsT();

  useEffect(() => {
    getFiltersRequest(projectId);
  }, []);

  useEffect(() => {
    if (!isFilterOpen) return;
    const handleEscape = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") setIsFilterOpen(false);
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isFilterOpen]);

  const handleReset = () => {
    resetFormData();
    getConfigurationsRequest(projectId, { attributes: [], labels: [] });
    setIsFilterOpen(false);
  };

  const handleApply = () => {
    getConfigurationsRequest(projectId, formData);
    setIsFilterOpen(false);
  };

  const totalFiltersApplied = getAllFilters(appliedFilter).length;

  const FilterCloseUI = (
    <Stack alignItems="center">
      <TooltipShortcut shortcut={{ action: () => setIsFilterOpen(!isFilterOpen), shortCutKey: "f", modifier: "alt" }}>
        <IconButton k={genericTranslationsK.open} size="small" onClick={() => setIsFilterOpen(!isFilterOpen)} sx={{ color: theme.palette.customColors.darkGrey }}>
          <DekoxIcon icon={isFilterOpen ? "doubleArrowRight" : "doubleArrowleft"} fontSize="small" />
        </IconButton>
      </TooltipShortcut>
      <Typography
        variant="body2"
        sx={{
          writingMode: "vertical-rl",
          transform: "rotate(360deg)",
          mt: 0.5,
          color: theme.palette.customColors.darkGrey,
          fontWeight: "normal"
        }}
      >
        {genericTranslationsT.filter} {totalFiltersApplied > 0 ? `(${totalFiltersApplied})` : ""}
      </Typography>
    </Stack>
  );

  const FilterOpenUI = (
    <Stack overflow="hidden" width="100%" py={2}>
      {/* Header */}
      <Stack flex={1} px={3} direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2" fontWeight={600}>
          {genericTranslationsT.filter}
        </Typography>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Stack gap={1} direction="row" alignItems="center">
            <TooltipShortcut shortcut={{ action: handleApply, shortCutKey: "a", modifier: "alt" }}>
              <Button color="success" t={genericTranslationsT} k={genericTranslationsK} field="apply" variant="outlined" size="small" onClick={handleApply} />
            </TooltipShortcut>
            <TooltipShortcut shortcut={{ action: handleReset, shortCutKey: "n", modifier: "alt" }}>
              <Button color="error" t={genericTranslationsT} k={genericTranslationsK} field="reset" variant="text" size="small" onClick={handleReset} />
            </TooltipShortcut>
          </Stack>
          <TooltipShortcut shortcut={{ action: () => setIsFilterOpen(false), shortCutKey: "f", modifier: "alt" }}>
            <IconButton k={genericTranslationsK.close} size="small" onClick={() => setIsFilterOpen(false)}>
              <DekoxIcon icon="doubleArrowRight" fontSize="small" />
            </IconButton>
          </TooltipShortcut>
        </Stack>
      </Stack>
      {/* Content */}
      <Stack overflow="auto" gap={2} mt={2} px={3} py={2}>
        {filterState.loading === "is-loading" && <FilterLoader />}
        {filterState.loading === "has-loaded" && (
          <>
            <LabelsFilter />
            <AttributesFilter />
          </>
        )}
      </Stack>
    </Stack>
  );

  return (
    <Box
      {...(!isFilterOpen ? getClickableProps(() => setIsFilterOpen(true)) : {})}
      sx={{
        transition: "width .5s",
        width: isFilterOpen ? FILTER_WIDTH.OPEN : FILTER_WIDTH.CLOSE,
        backgroundColor: theme.palette.customColors.white,
        borderLeft: `1px solid ${theme.palette.customColors.grey}`,
        borderTop: `1px solid ${theme.palette.customColors.grey}`,
        display: "flex",
        flexDirection: "column",
        alignItems: isFilterOpen ? "flex-start" : "center",
        pt: isFilterOpen ? 0 : 1.5,
        gap: 1,
        cursor: isFilterOpen ? "auto" : "pointer",
        outline: "none",
        ...(!isFilterOpen && { "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}` } })
      }}
      onClick={() => !isFilterOpen && setIsFilterOpen(true)}
    >
      {isFilterOpen ? FilterOpenUI : FilterCloseUI}
    </Box>
  );
};

export const Filter = connect(mapStateToProps, ownActions)(FilterBase);
